import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { ExternalLink, X } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import SearchableSelectInput from "./searchable-select-input";
import EntityAttachmentsList from "./entity-attachments-list";
import ExternalDocumentsSection from "./external-documents-section";
import {
  SiteServiceRequest,
  deleteSiteServiceRequestDocument,
  getInvoices,
  getQuotations,
  getSiteServiceRequest,
  setSiteServiceRequestInvoices,
  setSiteServiceRequestQuotations,
  uploadSiteServiceRequestDocument,
} from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";
import SidePanel, { PanelHeader } from "./side-panel";
import { useIsMobile } from "../hooks/use-is-mobile";

interface SiteServiceRequestDetailsDrawerProps {
  requestId: string | null;
  onClose: () => void;
}

const SiteServiceRequestDetailsDrawer: React.FC<
  SiteServiceRequestDetailsDrawerProps
> = ({ requestId, onClose }) => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [linkInvoiceId, setLinkInvoiceId] = useState("");
  const [linkQuotationId, setLinkQuotationId] = useState("");
  const [isSavingInvoices, setIsSavingInvoices] = useState(false);
  const [isSavingQuotation, setIsSavingQuotation] = useState(false);

  const requestQuery = useQuery({
    queryKey: ["site-services", "requests", requestId],
    queryFn: async () => getSiteServiceRequest(requestId!),
    enabled: !!requestId,
  });

  const invoicesQuery = useQuery({
    queryKey: [
      "invoices",
      "site-service-request-link",
      { page: 1, limit: 100 },
    ],
    queryFn: async () => getInvoices(1, 100, ""),
    enabled: !!requestId,
  });

  const quotationsQuery = useQuery({
    queryKey: [
      "quotations",
      "site-service-request-link",
      { page: 1, limit: 100 },
    ],
    queryFn: async () => getQuotations(1, 100, ""),
    enabled: !!requestId,
  });

  useEffect(() => {
    if (!requestId) return;
    setLinkInvoiceId("");
    setLinkQuotationId("");
  }, [requestId]);

  useEffect(() => {
    if (!requestQuery.error) return;
    toast.error(
      getBackendErrorMessage(requestQuery.error, "Failed to load request"),
    );
  }, [requestQuery.error]);

  const request: SiteServiceRequest | null = requestQuery.data ?? null;

  const linkedInvoiceIds = useMemo(
    () => new Set((request?.invoices ?? []).map((inv) => inv.id)),
    [request?.invoices],
  );

  const linkedQuotations = useMemo(
    () =>
      request?.quotations?.length
        ? request.quotations
        : request?.quotation
          ? [request.quotation]
          : [],
    [request?.quotations, request?.quotation],
  );

  const linkedQuotationIds = useMemo(
    () => new Set(linkedQuotations.map((q) => q.id)),
    [linkedQuotations],
  );

  const availableInvoiceOptions = useMemo(
    () =>
      (invoicesQuery.data?.data ?? [])
        .filter((inv) => !linkedInvoiceIds.has(inv.id))
        .map((inv) => ({
          value: inv.id,
          label: `${inv.reference} — ${inv.customer?.fullName ?? "Customer"}`,
        })),
    [invoicesQuery.data, linkedInvoiceIds],
  );

  const availableQuotationOptions = useMemo(
    () =>
      (quotationsQuery.data?.data ?? [])
        .filter((q) => !linkedQuotationIds.has(q.id))
        .map((q) => ({
          value: q.id,
          label: `${q.reference} — ${q.customer?.fullName ?? "Customer"}`,
        })),
    [quotationsQuery.data, linkedQuotationIds],
  );

  const refreshRequest = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["site-services", "requests", requestId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["site-services", "requests"],
      }),
    ]);
  };

  const handleLinkInvoice = async () => {
    if (!request?.id || !linkInvoiceId) return;
    setIsSavingInvoices(true);
    try {
      const nextIds = [
        ...(request.invoices ?? []).map((i) => i.id),
        linkInvoiceId,
      ];
      await setSiteServiceRequestInvoices(request.id, nextIds);
      toast.success("Invoice linked");
      setLinkInvoiceId("");
      await refreshRequest();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to link invoice"));
    } finally {
      setIsSavingInvoices(false);
    }
  };

  const handleUnlinkInvoice = async (invoiceId: string) => {
    if (!request?.id) return;
    setIsSavingInvoices(true);
    try {
      const nextIds = (request.invoices ?? [])
        .map((i) => i.id)
        .filter((id) => id !== invoiceId);
      await setSiteServiceRequestInvoices(request.id, nextIds);
      toast.success("Invoice unlinked");
      await refreshRequest();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to unlink invoice"));
    } finally {
      setIsSavingInvoices(false);
    }
  };

  const handleUnlinkAllInvoices = async () => {
    if (!request?.id) return;
    setIsSavingInvoices(true);
    try {
      await setSiteServiceRequestInvoices(request.id, []);
      toast.success("All invoices unlinked");
      await refreshRequest();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to unlink invoices"));
    } finally {
      setIsSavingInvoices(false);
    }
  };

  const handleLinkQuotation = async () => {
    if (!request?.id || !linkQuotationId) return;
    setIsSavingQuotation(true);
    try {
      const nextIds = [...linkedQuotations.map((q) => q.id), linkQuotationId];
      await setSiteServiceRequestQuotations(request.id, nextIds);
      toast.success("Quotation linked");
      setLinkQuotationId("");
      await refreshRequest();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to link quotation"));
    } finally {
      setIsSavingQuotation(false);
    }
  };

  const handleUnlinkQuotation = async (quotationId: string) => {
    if (!request?.id) return;
    setIsSavingQuotation(true);
    try {
      const nextIds = linkedQuotations
        .map((q) => q.id)
        .filter((id) => id !== quotationId);
      await setSiteServiceRequestQuotations(request.id, nextIds);
      toast.success("Quotation unlinked");
      await refreshRequest();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to unlink quotation"));
    } finally {
      setIsSavingQuotation(false);
    }
  };

  const handleUnlinkAllQuotations = async () => {
    if (!request?.id) return;
    setIsSavingQuotation(true);
    try {
      await setSiteServiceRequestQuotations(request.id, []);
      toast.success("All quotations unlinked");
      await refreshRequest();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to unlink quotations"));
    } finally {
      setIsSavingQuotation(false);
    }
  };

  return (
    <SidePanel open={!!requestId} onClose={onClose} width="w-[720px]">
      {requestId && (
        <div className="relative h-full flex flex-col">
          {isMobile ? (
            <PanelHeader
              title="Service Request Details"
              subtitle={request?.reference}
              onClose={onClose}
            />
          ) : (
            <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-[22px] font-semibold text-foreground font-jakarta">
                    Service Request Details
                  </h2>
                  {request && (
                    <p className="text-sm text-gray-500 mt-1">
                      {request.reference}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0 flex-shrink-0"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <ScrollArea className="flex-1">
            <div
              className={
                isMobile ? "p-4 pb-8 space-y-6" : "px-6 py-4 space-y-6"
              }
            >
              {requestQuery.isLoading ? (
                <p className="text-sm text-gray-500">Loading request...</p>
              ) : request ? (
                <>
                  {request.serviceType?.iconUrl ? (
                    <div className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                      <img
                        src={request.serviceType.iconUrl}
                        alt={request.serviceType.name ?? "Service type"}
                        className="h-48 w-full object-cover"
                      />
                    </div>
                  ) : null}

                  {isMobile ? (
                    <dl className="divide-y divide-gray-100 rounded-2xl border border-gray-100 bg-white">
                      <div className="flex items-start justify-between gap-4 px-4 py-3">
                        <dt className="shrink-0 text-sm text-gray-500">
                          Service
                        </dt>
                        <dd className="min-w-0 break-words text-right text-sm font-medium text-gray-900">
                          {request.serviceType?.name ?? "—"}
                        </dd>
                      </div>
                      <div className="flex items-start justify-between gap-4 px-4 py-3">
                        <dt className="shrink-0 text-sm text-gray-500">
                          Created
                        </dt>
                        <dd className="min-w-0 text-right text-sm font-medium text-gray-900">
                          {format(
                            new Date(request.createdAt),
                            "MMM dd, yyyy HH:mm",
                          )}
                        </dd>
                      </div>
                      <div className="flex items-start justify-between gap-4 px-4 py-3">
                        <dt className="shrink-0 text-sm text-gray-500">
                          Customer
                        </dt>
                        <dd className="min-w-0 break-words text-right text-sm font-medium text-gray-900">
                          {request.user?.fullName ?? "—"}
                          {request.user?.email && (
                            <p className="break-all text-xs font-normal text-gray-500">
                              {request.user.email}
                            </p>
                          )}
                        </dd>
                      </div>
                    </dl>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Service
                        </p>
                        <p className="text-sm font-medium mt-1">
                          {request.serviceType?.name ?? "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Created
                        </p>
                        <p className="text-sm font-medium mt-1">
                          {format(
                            new Date(request.createdAt),
                            "MMM dd, yyyy HH:mm",
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Customer
                        </p>
                        <p className="text-sm font-medium mt-1">
                          {request.user?.fullName ?? "—"}
                        </p>
                        {request.user?.email && (
                          <p className="text-xs text-gray-500">
                            {request.user.email}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3 pt-2 border-t border-gray-100">
                    <h3 className="text-base font-semibold">Attachments</h3>
                    <EntityAttachmentsList attachments={request.attachments} />
                  </div>

                  <div className="space-y-3 pt-2 border-t border-gray-100">
                    <h3 className="text-base font-semibold">Invoices</h3>
                    <p className="text-xs text-gray-500">
                      Link an existing system invoice, or upload an invoice
                      file.
                    </p>
                    {(request.invoices ?? []).length > 0 ? (
                      <div className="space-y-2">
                        {request.invoices.map((invoice) => (
                          <div
                            key={invoice.id}
                            className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50"
                          >
                            <div>
                              <p className="text-sm font-medium">
                                {invoice.reference}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Linked system invoice
                              </p>
                              {invoice.pdfUrl && (
                                <a
                                  href={invoice.pdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1 mt-1"
                                >
                                  View PDF <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-200 text-red-700 hover:bg-red-50"
                              disabled={isSavingInvoices}
                              onClick={() =>
                                void handleUnlinkInvoice(invoice.id)
                              }
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-200 text-red-700 hover:bg-red-50"
                          disabled={isSavingInvoices}
                          onClick={() => void handleUnlinkAllInvoices()}
                        >
                          Unlink all
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No system invoices linked.
                      </p>
                    )}

                    <div className="space-y-3 pt-2">
                      <p className="text-sm font-medium text-gray-700">
                        Link existing invoice
                      </p>
                      <div
                        className={
                          isMobile ? "w-full" : "min-w-[280px] max-w-md"
                        }
                      >
                        <SearchableSelectInput
                          options={availableInvoiceOptions}
                          value={linkInvoiceId}
                          defaultLabel={
                            invoicesQuery.isLoading
                              ? "Loading invoices..."
                              : availableInvoiceOptions.length === 0
                                ? "No more invoices to add"
                                : "Select an invoice"
                          }
                          onSelect={setLinkInvoiceId}
                        />
                      </div>
                      <Button
                        size="sm"
                        disabled={
                          isSavingInvoices ||
                          invoicesQuery.isLoading ||
                          !linkInvoiceId ||
                          availableInvoiceOptions.length === 0
                        }
                        onClick={() => void handleLinkInvoice()}
                      >
                        {isSavingInvoices ? "Saving..." : "Link invoice"}
                      </Button>
                    </div>

                    <ExternalDocumentsSection
                      title="Uploaded invoice files"
                      kind="INVOICE"
                      documents={request.uploadedInvoices ?? []}
                      canUpload
                      onUpload={async (file) => {
                        await uploadSiteServiceRequestDocument(
                          request.id,
                          "INVOICE",
                          file,
                        );
                        await refreshRequest();
                      }}
                      onDelete={async (documentId) => {
                        await deleteSiteServiceRequestDocument(
                          request.id,
                          documentId,
                        );
                        await refreshRequest();
                      }}
                    />
                  </div>

                  <div className="space-y-3 pt-2 border-t border-gray-100">
                    <h3 className="text-base font-semibold">Quotations</h3>
                    <p className="text-xs text-gray-500">
                      Link existing system quotations, or upload a quotation
                      file.
                    </p>
                    {linkedQuotations.length > 0 ? (
                      <div className="space-y-2">
                        {linkedQuotations.map((quotation) => (
                          <div
                            key={quotation.id}
                            className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50"
                          >
                            <div>
                              <p className="text-sm font-medium">
                                {quotation.reference}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Linked system quotation
                              </p>
                              {quotation.pdfUrl && (
                                <a
                                  href={quotation.pdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1 mt-1"
                                >
                                  View PDF <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-200 text-red-700 hover:bg-red-50"
                              disabled={isSavingQuotation}
                              onClick={() =>
                                void handleUnlinkQuotation(quotation.id)
                              }
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-200 text-red-700 hover:bg-red-50"
                          disabled={isSavingQuotation}
                          onClick={() => void handleUnlinkAllQuotations()}
                        >
                          Unlink all
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No system quotations linked.
                      </p>
                    )}

                    <div className="space-y-3 pt-2">
                      <p className="text-sm font-medium text-gray-700">
                        Link existing quotation
                      </p>
                      <div
                        className={
                          isMobile ? "w-full" : "min-w-[280px] max-w-md"
                        }
                      >
                        <SearchableSelectInput
                          options={availableQuotationOptions}
                          value={linkQuotationId}
                          defaultLabel={
                            quotationsQuery.isLoading
                              ? "Loading quotations..."
                              : availableQuotationOptions.length === 0
                                ? "No more quotations to add"
                                : "Select a quotation"
                          }
                          onSelect={setLinkQuotationId}
                        />
                      </div>
                      <Button
                        size="sm"
                        disabled={
                          isSavingQuotation ||
                          quotationsQuery.isLoading ||
                          !linkQuotationId ||
                          availableQuotationOptions.length === 0
                        }
                        onClick={() => void handleLinkQuotation()}
                      >
                        {isSavingQuotation ? "Saving..." : "Link quotation"}
                      </Button>
                    </div>

                    <ExternalDocumentsSection
                      title="Uploaded quotation files"
                      kind="QUOTATION"
                      documents={request.uploadedQuotations ?? []}
                      canUpload
                      onUpload={async (file) => {
                        await uploadSiteServiceRequestDocument(
                          request.id,
                          "QUOTATION",
                          file,
                        );
                        await refreshRequest();
                      }}
                      onDelete={async (documentId) => {
                        await deleteSiteServiceRequestDocument(
                          request.id,
                          documentId,
                        );
                        await refreshRequest();
                      }}
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">Request not found.</p>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </SidePanel>
  );
};

export default SiteServiceRequestDetailsDrawer;
